import { useCallback, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const ACCEPTED_EXTENSIONS = '.csv,.xlsx,.xls,.pdf'

interface UploadZoneProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onFilesSelected: (files: FileList) => void
}

export function UploadZone({ open, onOpenChange, onFilesSelected }: UploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const acceptFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList?.length) return
      onFilesSelected(fileList)
      onOpenChange(false)
    },
    [onFilesSelected, onOpenChange]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      acceptFiles(e.dataTransfer.files)
    },
    [acceptFiles]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl min-h-[480px]">
        <DialogHeader>
          <DialogTitle>Upload Bank Statement</DialogTitle>
        </DialogHeader>

        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          className={cn(
            'flex flex-1 min-h-[360px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors',
            isDragging
              ? 'border-blue-400 bg-blue-50'
              : 'border-neutral-300 bg-neutral-50 hover:border-neutral-400 hover:bg-neutral-100'
          )}
        >
          <Upload
            className={cn(
              'mb-3 h-10 w-10',
              isDragging ? 'text-blue-500' : 'text-neutral-400'
            )}
          />
          <p className="text-sm font-medium text-neutral-700">
            Drop a bank statement here, or click to browse
          </p>
          <p className="mt-1 text-xs text-neutral-500">CSV, Excel, or PDF</p>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXTENSIONS}
            className="hidden"
            multiple
            onChange={(e) => acceptFiles(e.target.files)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
